import { ImCrop } from '@icon/react-icons';
import {
  Dialog,
  DialogTitle,
  Grid,
  Typography,
  DialogContent,
  Slider,
  DialogActions,
  Button,
} from '@material-ui/core';
import { useState, useRef } from 'react';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import AvatarEditor, { AvatarEditorProps } from 'react-avatar-editor';
import { useCallback } from 'react';
import { useSnackbarInstance } from '@lib/common';

export interface ImageEditorProps extends Omit<AvatarEditorProps, 'scale'> {
  open: boolean;
  onClose: () => any;
  onFinish?: (props: { zoom: number; imageCanvas: HTMLCanvasElement }) => any;
  zoomStep?: number;
  zoomMax?: number;
  zoomMin?: number;
}

export const ImageEditor = ({
  open,
  onClose,
  onFinish,
  zoomMax = 3,
  zoomMin = 0.1,
  zoomStep = 0.1,
  ...props
}: ImageEditorProps) => {
  const [zoom, setZoom] = useState(1.5);

  const avatarPicker = useRef<AvatarEditor>(null);

  const updateZoom = useCallback(
    (event: any, newValue: number) => {
      setZoom(newValue as number);
    },
    [setZoom]
  );

  const closeImagePicker = useCallback(() => {
    onClose && onClose();
  }, [onClose]);

  const { enqueue: showError } = useSnackbarInstance();

  const onDone = useCallback(() => {
    let imageCanvas: HTMLCanvasElement;
    try {
      imageCanvas = avatarPicker.current?.getImage();
    } catch {
      showError(
        <Typography variant="body2" style={{ maxWidth: '50vw' }}>
          The image is not ready to be edited yet. This usually happens because of the browser is
          not able to read the image fast enough. Please wait for some time for the image to show up
          in the editor.
          <br /> Report this error if it takes to much time for the image to become editable.
        </Typography>,
        {
          autoHideDuration: 5000,
          variant: 'error',
        }
      );
      return;
    }
    const result = {
      zoom,
      imageCanvas,
    };
    closeImagePicker();
    onFinish && onFinish(result);
  }, [closeImagePicker, onFinish, zoom, showError]);

  const zoomEvent = useCallback(
    (e: WheelEvent) => {
      setZoom(z => {
        const forward = e.deltaY < 0;
        if (forward) {
          return z >= zoomMax ? zoomMax : z + zoomStep;
        }
        return z <= zoomMin ? zoomMin : z - zoomStep;
      });
    },
    [setZoom, zoomMax, zoomMin, zoomStep]
  );

  return (
    <Dialog keepMounted fullWidth maxWidth="sm" open={open} onClose={closeImagePicker}>
      <DialogTitle disableTypography>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <ImCrop color="secondary" />
          </Grid>
          <Grid item xs>
            <Typography variant="h6" style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
              Edit Image
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Adjust image to fit in the given shadowed area.
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item>
            <div style={{ textAlign: 'center' }} onWheel={zoomEvent as any}>
              <AvatarEditor {...props} ref={avatarPicker} scale={zoom} />
              {/* tainted canvas error comes up if u are editing the source  image without uploading directly due to security reasons */}
              {/* To implement: https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image for making the initial edit possible of logos and posters */}
            </div>
          </Grid>
          <Grid item xs>
            <Typography>Zoom</Typography>
            <Grid container spacing={1}>
              <Grid item>
                <ZoomOutIcon />
              </Grid>
              <Grid item xs>
                <Slider
                  value={zoom}
                  onChange={updateZoom}
                  step={zoomStep}
                  max={zoomMax}
                  min={zoomMin}
                />
              </Grid>
              <Grid item>
                <ZoomInIcon />
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textSecondary">
                🚧: Other options under construction: 🚧
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={closeImagePicker} color="primary">
          Cancel
        </Button>
        <Button onClick={onDone} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
